// Boolean CSG for Model Studio, on top of gkjohnson's three-bvh-csg
// (docs/3d-content-pipeline/roadmap.md 3.1 Phase 2 - the roadmap's own pick
// for boolean ops, chosen over @react-three/csg because that wrapper's own
// README still says "work in progress"; this uses the underlying library
// directly). Shared by the live editor (raw design units) and
// buildModelGroup's GLB export bake (metres, grounded) - both hand this
// module elements already converted into whatever coordinate space they
// want the result in, so evaluateModelCsg itself stays unit-agnostic.
import { ADDITION, Brush, Evaluator, INTERSECTION, SUBTRACTION } from 'three-bvh-csg';
import { MeshStandardMaterial } from 'three';
import { createUnitModelGeometry } from './modelPrimitives';
import type { ModelGroupElement } from './modelGroup';

/** Every shape is either solid ('add', the default), a hole ('subtract'), or
 * clips the running result down to their overlap ('intersect') - the same
 * "every shape is solid or a hole" model Tinkercad uses, per the roadmap's
 * own survey. */
export type ModelCsgOp = 'add' | 'subtract' | 'intersect';
export const MODEL_CSG_OPS: ModelCsgOp[] = ['add', 'subtract', 'intersect'];

const OP_CODE: Record<ModelCsgOp, number> = { add: ADDITION, subtract: SUBTRACTION, intersect: INTERSECTION };

const DEG_TO_RAD = Math.PI / 180;

/**
 * True when the boolean evaluator actually needs to run - an all-'add'
 * (or unset) list is a plain union of non-overlapping shapes, which is
 * exactly what every element already rendered as before CSG existed, so
 * that fast, zero-evaluator-cost path stays the default for every project
 * that never touches the Op picker.
 */
export function hasCsgOps(elements: { csgOp?: ModelCsgOp }[]): boolean {
  return elements.some((el) => el.csgOp && el.csgOp !== 'add');
}

/**
 * Tinkercad's own documented performance cliff sits around 30 shapes; past
 * that, live per-frame boolean evaluation in an interactive editor stops
 * being a good trade, so callers should stop invoking this and fall back to
 * rendering elements individually (ops still apply correctly at export,
 * which runs once, not per frame).
 */
export const MODEL_CSG_LIVE_ELEMENT_CAP = 30;

function applyTransform(mesh: Brush, el: ModelGroupElement): void {
  mesh.scale.set(el.w, el.h, el.d);
  mesh.position.set(el.x, el.y + el.h / 2, el.z);
  const rotation = el.rotation ?? [0, 0, 0];
  mesh.rotation.set(rotation[0] * DEG_TO_RAD, rotation[1] * DEG_TO_RAD, rotation[2] * DEG_TO_RAD);
  mesh.updateMatrixWorld(true);
}

/**
 * Evaluate the whole element list as one CSG tree: the first element is
 * always the base (its own `csgOp` is ignored - there is nothing before it
 * to combine with), and each following element combines into the running
 * result via its own `csgOp`, sequentially left to right:
 * `evaluate(evaluate(evaluate(base, c0, c0.op), c1, c1.op), ...)`, via
 * repeated calls to `Evaluator.evaluate` (the library's own simplest,
 * directly-documented entry point - `evaluateHierarchy` wraps the same
 * sequential fold over an Object3D tree of `Operation`s, but folding by
 * hand here keeps the transform/material bookkeeping in one place). Each
 * material carries its source element's array index as
 * `userData.modelElementIndex`, so a caller that raycasts the result can
 * map a hit straight back to the element that produced it via
 * `elements[hitMaterial.userData.modelElementIndex]`, even though
 * `useGroups`'s default group consolidation/unused-material removal can
 * reorder or drop entries from the material array itself (the index tag
 * survives on the surviving material regardless of its final array
 * position).
 */
export function evaluateModelCsg(elements: ModelGroupElement[]): Brush {
  const evaluator = new Evaluator();

  const materialFor = (el: ModelGroupElement, index: number) => {
    const material = new MeshStandardMaterial({ color: el.colour, roughness: 0.7, metalness: 0.05 });
    material.userData.modelElementIndex = index;
    return material;
  };

  const brushFor = (el: ModelGroupElement, index: number) => {
    const brush = new Brush(createUnitModelGeometry(el.shape ?? 'box', el.profile), materialFor(el, index));
    applyTransform(brush, el);
    return brush;
  };

  const [first, ...rest] = elements;
  let result = brushFor(first!, 0);
  rest.forEach((el, i) => {
    const child = brushFor(el, i + 1);
    result = evaluator.evaluate(result, child, OP_CODE[el.csgOp ?? 'add']);
    result.updateMatrixWorld(true);
  });

  return result;
}
